import { computed, markRaw, ref, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import { ElMessage } from 'element-plus'
import { cloneCaptureProfile } from '@/core/capture-profile'
import { SessionEngine } from '@/core/session-engine'
import { EfficiencySegmentTracker } from '@/core/efficiency-segment-tracker'
import type { CaptureProfile, GameStateSnapshot, LiveRateSnapshot, MetricKey, RankSubmissionDraft, RawReading, SessionRecord } from '@/domain/types'
import { i18n } from '@/i18n'
import { PresetRegistry } from '@/presets/registry'
import type { GamePreset, HudLocation } from '@/presets/types'
import { AnalysisPipeline, type PipelineResult } from '@/services/analysis-pipeline'
import { captureService, type CaptureStatus } from '@/services/capture-service'
import { db, RecoveryBuffer } from '@/services/database'
import { ensureOcrModelsCached, type OcrCacheProgress } from '@/services/ocr-resource-cache'
import { pipService } from '@/services/pip-service'
import { enqueueRankSubmission } from '@/services/rank-api'
import { useAppStore } from './app'

export type OcrStatus = 'checking' | 'downloading' | 'initializing' | 'ready' | 'error'
export type OcrStage = 'checking' | 'detection' | 'recognition' | 'initializing' | 'ready'

export const useLiveStore = defineStore('live', () => {
  const app = useAppStore()
  const preset = shallowRef<GamePreset | null>(null)
  const profile = shallowRef<CaptureProfile | null>(null)
  const session = shallowRef<SessionRecord | null>(null)
  const liveRates = shallowRef<LiveRateSnapshot | null>(null)
  const gameState = shallowRef<GameStateSnapshot | null>(null)
  const rawReading = shallowRef<RawReading | null>(null)
  const hudLocation = shallowRef<HudLocation | null>(null)
  const captureStatus = ref<CaptureStatus>(captureService.status)
  const captureLabel = ref('')
  const error = ref<string | null>(null)
  const needsCalibration = ref(false)
  const ocrStatus = ref<OcrStatus>('checking')
  const ocrStage = ref<OcrStage>('checking')
  const ocrProgress = ref(0)
  const ocrCachedModels = ref(0)
  const ocrError = ref<string | null>(null)
  const trend = ref<{ at: number; exp: number | null; hp: number | null; mp: number | null; mesos: number | null }[]>([])
  const video = markRaw(captureService.video)
  const recovery = markRaw(new RecoveryBuffer())
  let pipeline: AnalysisPipeline | null = null
  let sessionEngine: SessionEngine | null = null
  let rankTracker: EfficiencySegmentTracker | null = null
  let calibrationMetric: MetricKey | null = null
  let captureGeneration = 0
  let ocrInitialization: Promise<void> | null = null
  let readyOcrFingerprint = ''
  let initialized = false

  const isCapturing = computed(() => captureStatus.value === 'active')
  const isRunning = computed(() => session.value?.status === 'running')
  const isPaused = computed(() => session.value?.status === 'paused')

  async function initialize(): Promise<void> {
    if (initialized) return
    initialized = true
    try {
      await loadPreset(app.presetId, false)
      captureService.addEventListener('status', ((event: CustomEvent<CaptureStatus>) => {
        captureStatus.value = event.detail
      }) as EventListener)
      captureService.addEventListener('ended', () => {
        void interruptCapture().catch((caught: unknown) => {
          error.value = caught instanceof Error ? caught.message : String(caught)
        })
      })
    } catch (caught) {
      initialized = false
      error.value = caught instanceof Error ? `${caught.name}: ${caught.message}` : String(caught)
      ocrStatus.value = 'error'
      ocrError.value = error.value
      return
    }
    await initializeOcr()
  }

  async function loadPreset(id: string, initializeResources = initialized): Promise<void> {
    if (session.value && session.value.status !== 'ended' && preset.value?.id !== id) {
      throw new Error('End the active record before changing presets')
    }
    pipeline?.dispose()
    pipeline = null
    preset.value = await PresetRegistry.load(id)
    app.presetId = id
    sessionEngine = new SessionEngine(preset.value)
    liveRates.value = null
    rankTracker = new EfficiencySegmentTracker(preset.value)
    const exactProfiles = await db.profiles.where('[presetId+presetVersion]').equals([id, preset.value.version]).toArray()
    const exact = exactProfiles.reduce<CaptureProfile | null>((latest, item) => !latest || item.updatedAt > latest.updatedAt ? item : latest, null)
    const previous = exact ? null : await db.profiles.orderBy('updatedAt').reverse().filter((item) => item.presetId === id).first()
    const migrated = previous ? preset.value.migrateProfile(previous) : null
    needsCalibration.value = Boolean(previous && !migrated)
    profile.value = exact ?? migrated ?? createProfile(preset.value)
    await db.profiles.put(profile.value)
    if (initializeResources) await initializeOcr()
  }

  async function selectWindow(): Promise<void> {
    error.value = null
    try {
      if (session.value?.status === 'running') await interruptCapture()
      const info = await captureService.selectWindow()
      captureGeneration += 1
      captureLabel.value = info.label
      if (!preset.value || !profile.value) return
      profile.value = { ...profile.value, sourceWidth: info.width, sourceHeight: info.height, updatedAt: Date.now() }
      await db.profiles.put(profile.value)
      startPipeline(session.value?.id ?? 'preview', null, captureGeneration)
      if (session.value?.status === 'interrupted' && sessionEngine) {
        const resumedAt = Date.now()
        sessionEngine.resume(resumedAt, captureGeneration)
        pipeline?.resetBaseline()
        session.value = sessionEngine.current(resumedAt)
        liveRates.value = sessionEngine.liveRates(resumedAt)
        await persistSession()
      }
    } catch (caught) {
      const message = caught instanceof Error ? caught.message : String(caught)
      const name = typeof caught === 'object' && caught !== null && 'name' in caught ? String(caught.name) : ''
      const permissionDenied = name === 'NotAllowedError' || /permission denied|not allowed by the user/i.test(message)
      ElMessage({
        type: permissionDenied ? 'warning' : 'error',
        message: String(i18n.global.t(permissionDenied ? 'live.capturePermissionDenied' : 'live.captureFailed', { reason: message })),
        duration: 4_500,
        showClose: true,
      })
    }
  }

  async function startSession(): Promise<void> {
    if (!isCapturing.value) await selectWindow()
    if (!isCapturing.value || !profile.value || !sessionEngine) return
    const startedAt = Date.now()
    captureGeneration += 1
    session.value = sessionEngine.start(profile.value.id, startedAt, null, captureGeneration)
    liveRates.value = sessionEngine.liveRates(startedAt)
    rankTracker?.reset()
    await db.sessions.put(session.value)
    trend.value = gameState.value ? [{
      at: startedAt,
      exp: gameState.value.exp.ratio,
      hp: gameState.value.hp.ratio,
      mp: gameState.value.mp.ratio,
      mesos: gameState.value.mesos,
    }] : []
    startPipeline(session.value.id, null, captureGeneration)
  }

  async function pauseSession(): Promise<void> {
    if (!sessionEngine) return
    queueCompletedSegment(rankTracker?.finish() ?? null)
    const pausedAt = Date.now()
    session.value = sessionEngine.pause(pausedAt)
    liveRates.value = sessionEngine.liveRates(pausedAt)
    await db.sessions.put(session.value)
  }

  async function resumeSession(): Promise<void> {
    if (!sessionEngine) return
    const resumedAt = Date.now()
    captureGeneration += 1
    session.value = sessionEngine.resume(resumedAt, captureGeneration)
    rankTracker?.reset()
    pipeline?.setCaptureGeneration(captureGeneration)
    pipeline?.resetBaseline()
    session.value = sessionEngine.current(resumedAt)
    liveRates.value = sessionEngine.liveRates(resumedAt)
    if (session.value) await db.sessions.put(session.value)
  }

  async function endSession(): Promise<void> {
    if (!sessionEngine || !session.value || session.value.status === 'ended') return
    const endedAt = Date.now()
    queueCompletedSegment(rankTracker?.finish(endedAt) ?? null)
    session.value = sessionEngine.end(endedAt)
    liveRates.value = sessionEngine.liveRates(endedAt)
    await recovery.flush(session.value, true)
  }

  async function updateProfile(next: CaptureProfile): Promise<void> {
    const sourceChanged = profile.value ? captureSourceChanged(profile.value, next) : true
    profile.value = { ...cloneCaptureProfile(next), updatedAt: Date.now() }
    needsCalibration.value = false
    pipeline?.updateProfile(profile.value)
    if (sourceChanged) {
      captureGeneration += 1
      pipeline?.setCaptureGeneration(captureGeneration)
      pipeline?.resetBaseline()
    }
    await db.profiles.put(profile.value)
  }

  function setCalibrationMetric(metric: MetricKey | null): void {
    calibrationMetric = metric
    pipeline?.setDiagnosticMetric(metric)
  }

  async function openPip(): Promise<void> {
    await pipService.open(() => ({
      session: session.value,
      state: gameState.value,
      liveRates: liveRates.value,
      canStart: isCapturing.value && ocrStatus.value === 'ready',
    }), {
      start: startSession,
      pause: pauseSession,
      resume: resumeSession,
      end: endSession,
    }, app.locale, (level) => preset.value?.experienceToNext(level) ?? null)
  }

  async function initializeOcr(): Promise<void> {
    if (!preset.value || !profile.value) return
    if (ocrInitialization) return ocrInitialization
    const targetPreset = preset.value
    const targetProfile = profile.value
    const fingerprint = `${targetPreset.remoteResources.ocrModels.detection.url}@${targetPreset.remoteResources.ocrModels.detection.version}|${targetPreset.remoteResources.ocrModels.recognition.url}@${targetPreset.remoteResources.ocrModels.recognition.version}`
    if (ocrStatus.value === 'ready' && readyOcrFingerprint === fingerprint) return

    ocrInitialization = (async () => {
      ocrStatus.value = 'checking'
      ocrStage.value = 'checking'
      ocrProgress.value = 4
      ocrCachedModels.value = 0
      ocrError.value = null
      const cached = new Set<string>()
      const recognizer = targetPreset.createRecognizer(targetProfile)
      let pendingProgress: OcrCacheProgress | null = null
      let progressFrame = 0
      const applyProgress = () => {
        progressFrame = 0
        const progress = pendingProgress
        pendingProgress = null
        if (!progress) return
        ocrStatus.value = 'downloading'
        ocrStage.value = progress.model
        ocrProgress.value = Math.max(5, Math.round(5 + progress.progress * 70))
        ocrCachedModels.value = cached.size
      }
      try {
        await ensureOcrModelsCached(targetPreset.remoteResources.ocrModels, (progress) => {
          if (progress.fromCache) cached.add(progress.model)
          pendingProgress = progress
          if (!progressFrame) progressFrame = requestAnimationFrame(applyProgress)
        })
        if (progressFrame) cancelAnimationFrame(progressFrame)
        applyProgress()
        ocrStatus.value = 'initializing'
        ocrStage.value = 'initializing'
        ocrProgress.value = 82
        await recognizer.warmup()
        readyOcrFingerprint = fingerprint
        ocrStage.value = 'ready'
        ocrStatus.value = 'ready'
        ocrProgress.value = 100
      } catch (caught) {
        if (progressFrame) cancelAnimationFrame(progressFrame)
        pendingProgress = null
        ocrStatus.value = 'error'
        ocrError.value = caught instanceof Error ? caught.message : String(caught)
      } finally {
        recognizer.dispose()
      }
    })().finally(() => {
      ocrInitialization = null
    })
    return ocrInitialization
  }

  async function interruptCapture(): Promise<void> {
    if (!sessionEngine || session.value?.status !== 'running') return
    pipeline?.stop()
    const interruptedAt = Date.now()
    queueCompletedSegment(rankTracker?.finish(interruptedAt) ?? null)
    session.value = sessionEngine.interrupt(interruptedAt)
    liveRates.value = sessionEngine.liveRates(interruptedAt)
    await persistSession()
  }

  async function persistSession(): Promise<void> {
    if (!session.value) return
    await db.sessions.put(session.value)
  }

  function startPipeline(sessionId: string, baseline: GameStateSnapshot | null = null, generation = captureGeneration): void {
    if (!preset.value || !profile.value) return
    pipeline?.dispose()
    pipeline = markRaw(new AnalysisPipeline(preset.value, profile.value, onPipelineResult))
    pipeline.setDiagnosticMetric(calibrationMetric)
    pipeline.start(video, sessionId, baseline, generation)
  }

  function onPipelineResult(result: PipelineResult): void {
    if (result.captureGeneration !== captureGeneration) return
    rawReading.value = result.raw
    gameState.value = result.state
    hudLocation.value = result.hud
    const point = {
      at: result.state.capturedAt,
      exp: result.state.exp.ratio,
      hp: result.state.hp.ratio,
      mp: result.state.mp.ratio,
      mesos: result.state.mesos,
    }
    trend.value = [...trend.value.slice(-599), point]
    if (session.value?.status !== 'running' || !sessionEngine) return
    const ingested = sessionEngine.ingestWithAccounting(result.state, result.events, result.observation, result.captureGeneration)
    session.value = ingested.record
    liveRates.value = sessionEngine.liveRates(result.state.capturedAt)
    queueCompletedSegment(rankTracker?.ingest(result.state, ingested.accounting) ?? null)
    void recovery.flush(session.value).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }

  function queueCompletedSegment(submission: RankSubmissionDraft | null): void {
    if (!submission) return
    void enqueueRankSubmission(submission).catch((caught: unknown) => {
      error.value = caught instanceof Error ? caught.message : String(caught)
    })
  }

  function createProfile(gamePreset: GamePreset): CaptureProfile {
    const now = Date.now()
    return {
      id: crypto.randomUUID(),
      name: 'Default',
      presetId: gamePreset.id,
      presetVersion: gamePreset.version,
      sourceWidth: 1368,
      sourceHeight: 800,
      hudRect: gamePreset.hud.fallback,
      roiOverrides: {},
      colorCalibration: {},
      recognition: { captureFps: 4, minConfidence: 0.62, ocrChangeThreshold: 0.025 },
      createdAt: now,
      updatedAt: now,
    }
  }

  function captureSourceChanged(previous: CaptureProfile, next: CaptureProfile): boolean {
    return previous.sourceWidth !== next.sourceWidth
      || previous.sourceHeight !== next.sourceHeight
      || JSON.stringify(previous.hudRect) !== JSON.stringify(next.hudRect)
      || JSON.stringify(previous.roiOverrides) !== JSON.stringify(next.roiOverrides)
      || JSON.stringify(previous.colorCalibration) !== JSON.stringify(next.colorCalibration)
      || previous.recognition.minConfidence !== next.recognition.minConfidence
      || previous.recognition.ocrChangeThreshold !== next.recognition.ocrChangeThreshold
  }

  return {
    preset, profile, session, liveRates, gameState, rawReading, hudLocation, captureStatus, captureLabel, error, needsCalibration,
    ocrStatus, ocrStage, ocrProgress, ocrCachedModels, ocrError, trend, video,
    isCapturing, isRunning, isPaused, pipSupported: pipService.supported,
    initialize, loadPreset, selectWindow, startSession, pauseSession, resumeSession, endSession, updateProfile, setCalibrationMetric, openPip, initializeOcr,
  }
})
